import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Badge,
  Card,
  CardBody,
  CardHeader,
  Col,
  Collapse,
  Container,
  Row,
} from 'reactstrap'
import FeatherIcon from 'feather-icons-react'
import ImageAddVocabulary from '../../assets/images/extension/add_vocabulary_dark.png'
import ImageHighlight from '../../assets/images/v2/Highlight.png'
import ImageVocabularyList from '../../assets/images/v2/List.png'
import ImagePacks from '../../assets/images/v2/Packs.png'
import ImageQuiz from '../../assets/images/v2/quiz.png'
import ImageRephrase from '../../assets/images/v2/Rephrase.png'
import BackToTop from '../../components/Layout/backToTop'
import { FREE_VERSION } from '../../config'
import Pricing from './Pricing'
import Testi from './Testi'
import Section from './Section'

const installHref =
  'https://chrome.google.com/webstore/detail/highvocab-vocabulary-buil/nmalijlbcckplnamalenpdaafacfeemd?hl=en'

const steps = [
  {
    title: 'Read normally',
    description:
      'Browse articles, emails, or study material as usual until you notice a word worth keeping.',
    icon: 'search',
  },
  {
    title: 'Save it instantly',
    description:
      'Highlight the word and HighVocab suggests the meaning, pronunciation, and example for a quick one-click save.',
    icon: 'plus-circle',
  },
  {
    title: 'Remember it faster',
    description:
      'Saved words reappear while you browse and inside review tools, so repetition happens without extra study friction.',
    icon: 'repeat',
  },
]

const valueCards = [
  {
    title: 'Understand instantly',
    description: 'Meaning, pronunciation, and examples inline.',
    icon: 'zap',
  },
  {
    title: 'Keep words organized',
    description: 'Your saved vocabulary stays searchable and easy to review.',
    icon: 'folder',
  },
  {
    title: 'Remember more',
    description: 'Review tools and repeated exposure help words stick.',
    icon: 'book-open',
  },
]

const featureStories = [
  {
    title: 'Add words directly from any page',
    description:
      'Highlight any unfamiliar word and save it with the right meaning, pronunciation, and example in seconds.',
    image: ImageAddVocabulary,
    imageAlt: 'Adding a new vocabulary word from a webpage in HighVocab',
  },
  {
    title: 'See saved words highlighted as you browse',
    description:
      'Saved words show up again in context across the web, turning normal reading into automatic review.',
    image: ImageHighlight,
    imageAlt: 'Saved words highlighted on a webpage in HighVocab',
    reverse: true,
  },
  {
    title: 'Keep your vocabulary list clean and easy to review',
    description:
      'See your saved words in one organized list with search, meanings, pronunciation, and progress so review feels simple.',
    image: ImageVocabularyList,
    imageAlt: 'HighVocab vocabulary list with saved words and review details',
  },
  {
    title: 'Practice with packs, quizzes, and writing support',
    description:
      'Reinforce what you save with word packs, short quizzes, and writing support that makes practice feel useful.',
    image: ImageQuiz,
    imageAlt: 'HighVocab quiz interface for vocabulary review',
    reverse: true,
  },
]

const extraBenefits = [
  {
    title: 'Ready-made packs',
    description: 'Turn on themed packs to surface practical vocabulary automatically while you browse.',
    icon: 'layers',
    image: ImagePacks,
  },
  {
    title: 'Quick review loops',
    description: 'Reinforce saved words with short review moments instead of long, separate study sessions.',
    icon: 'clock',
    image: ImageQuiz,
  },
  {
    title: 'Clearer writing',
    description: 'Rewrite selected sentences into more natural English when you want extra help expressing ideas clearly.',
    icon: 'edit-3',
    image: ImageRephrase,
  },
]

const faqs = [
  {
    id: 'how-it-works',
    question: 'How does HighVocab work while I browse?',
    answer:
      'As you read normally, you can save useful words in one click, see meanings and examples inline, and revisit them later through highlights and review tools.',
  },
  {
    id: 'quick-start',
    question: 'How quickly can I start using it?',
    answer:
      'You can start in minutes. Install the extension, pin it to your browser, and begin saving words the first time you see one you want to remember.',
  },
  {
    id: 'browsers',
    question: 'Which browsers does HighVocab support?',
    answer:
      'HighVocab is available for Chrome, Edge, and Firefox.',
  },
  {
    id: 'languages',
    question: 'What languages does it support?',
    answer:
      'HighVocab supports translations in more than 60 languages. You can use it to improve English or to learn other languages too, since saved words show translations on hover and can be reviewed later.',
  },
  {
    id: 'save-word',
    question: 'How do I save a new word?',
    answer:
      'Highlight a word on the page and HighVocab will suggest the meaning, pronunciation, and example so you can save it in one quick step.',
  },
  {
    id: 'review-tools',
    question: 'How does HighVocab help me remember words later?',
    answer:
      'Saved words can reappear while you browse, and you can reinforce them with review tools like quizzes, packs, reminders, and organized vocabulary lists.',
  },
  {
    id: 'notes',
    question: 'Can I save notes and phrases too?',
    answer:
      'Yes. You can keep notes alongside your saved vocabulary so useful phrases, context, and ideas stay organized in one place.',
  },
  {
    id: 'free-plan',
    question: 'Is there a free plan?',
    answer:
      'Yes. You can start with the free plan and upgrade later if you want more saving, highlighting, and review capacity.',
  },
  {
    id: 'export',
    question: 'Can I export my vocabulary list?',
    answer:
      'Yes. HighVocab supports CSV export so you can open your saved vocabulary and notes in Excel or similar tools.',
  },
]

const Index = () => {
  const [openFaq, setOpenFaq] = useState('how-it-works')

  useEffect(() => {
    document.body.classList = ''

    const scrollNavigation = () => {
      const doc = document.documentElement
      const navBar = document.getElementById('topnav')
      const top =
        (window.pageYOffset || doc.scrollTop) - (doc.clientTop || 0)

      if (navBar != null) {
        if (top > 80) {
          navBar.classList.add('nav-sticky')
        } else {
          navBar.classList.remove('nav-sticky')
        }
      }
    }

    window.addEventListener('scroll', scrollNavigation, true)
    return () => window.removeEventListener('scroll', scrollNavigation, true)
  }, [])

  return (
    <React.Fragment>
      <Section />

      <div className="position-relative">
        <div className="shape overflow-hidden text-light">
          <svg
            viewBox="0 0 2880 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0 48H1437.5H2880V0H2160C1442.5 52 720 0 720 0H0V48Z"
              fill="currentColor"
            ></path>
          </svg>
        </div>
      </div>

      <div className="extension-landing">
        <Container className="mt-100 mt-60 md-padding-20">
          <div className="extension-proof-banner">
            <Row className="align-items-center gy-4">
              <Col lg={5}>
                <Badge pill className="extension-badge extension-badge-inverse mb-3">
                  Why people install HighVocab
                </Badge>
                <h2>Understand words faster now, remember them better later.</h2>
                <p className="extension-proof-copy mb-0">
                  HighVocab fits into the reading you already do, so vocabulary
                  growth feels natural instead of like extra homework.
                </p>
              </Col>
              <Col lg={7}>
                <div className="extension-proof-grid">
                  <div className="extension-proof-item">
                    <strong>No extra study workflow</strong>
                    <span>Learning happens inside your normal reading habit.</span>
                  </div>
                  <div className="extension-proof-item">
                    <strong>Instant word clarity</strong>
                    <span>Definitions, pronunciation, and examples appear right where you are.</span>
                  </div>
                  <div className="extension-proof-item">
                    <strong>Built to help memory stick</strong>
                    <span>Repeated exposure, review tools, and reminders keep words from fading.</span>
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </Container>

        <Container className="mt-100 mt-60 md-padding-20">
          <Row className="justify-content-center">
            <Col lg={8} className="text-center">
              <div className="section-title extension-section-intro">
                <Badge pill className="extension-badge mb-3">
                  How it works
                </Badge>
                <h3 className="title extension-section-heading mb-4">
                  Learn while you browse, not in a separate study session
                </h3>
                <p className="sec-title extension-section-lead">
                  HighVocab turns useful words into a lightweight habit:
                  notice them, save them, and meet them again naturally.
                </p>
              </div>
            </Col>
          </Row>

          <Row className="g-4 mt-4">
            {steps.map((step, index) => (
              <Col lg={4} md={6} key={step.title}>
                <div className="extension-card extension-step-card h-100">
                  <div className="extension-step-top">
                    <span className="extension-step-number">0{index + 1}</span>
                    <span className="extension-icon-wrap">
                      <FeatherIcon icon={step.icon} size={20} />
                    </span>
                  </div>
                  <h4>{step.title}</h4>
                  <p>{step.description}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>

        <Container className="mt-100 mt-60 md-padding-20">
          <Row className="justify-content-center">
            <Col lg={8} className="text-center">
              <div className="section-title extension-section-intro">
                <Badge pill className="extension-badge mb-3">
                  Core benefits
                </Badge>
                <h3 className="title extension-section-heading mb-4">
                  Why it feels useful right away
                </h3>
                <p className="sec-title extension-section-lead">
                  Three things matter most: instant clarity, simple saving, and
                  better recall over time.
                </p>
              </div>
            </Col>
          </Row>

          <Row className="g-4 mt-2">
            {valueCards.map((card) => (
              <Col lg={4} md={6} key={card.title}>
                <div className="extension-card h-100">
                  <span className="extension-icon-wrap mb-4">
                    <FeatherIcon icon={card.icon} size={20} />
                  </span>
                  <h4>{card.title}</h4>
                  <p>{card.description}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>

        {featureStories.map((feature, index) => (
          <Container className="mt-100 mt-60 md-padding-20" key={feature.title}>
            <Row className="align-items-center gy-4">
              <Col
                lg={6}
                md={6}
                className={`text-center ${feature.reverse ? 'order-md-2' : ''}`}
              >
                <div className="extension-image-frame">
                  <img
                    src={feature.image}
                    className="img-fluid sm-images"
                    alt={feature.imageAlt}
                  />
                </div>
              </Col>

              <Col
                lg={6}
                md={6}
                className={`mt-4 mt-sm-0 pt-2 pt-sm-0 ${
                  feature.reverse ? 'order-md-1' : ''
                }`}
              >
                <div
                  className={`section-title extension-feature-copy ${
                    feature.reverse ? 'me-lg-5' : 'ms-lg-5'
                  }`}
                >
                  <Badge pill className="extension-badge mb-3">
                    {index === 0
                      ? 'Capture'
                      : index === 1
                      ? 'Reinforce'
                      : index === 2
                      ? 'Organize'
                      : 'Practice'}
                  </Badge>
                  <h3 className="title extension-section-heading mb-4">
                    {feature.title}
                  </h3>
                  <p className="sec-title extension-section-lead">
                    {feature.description}
                  </p>
                </div>
              </Col>
            </Row>
          </Container>
        ))}

        <Container className="mt-100 mt-60 md-padding-20">
          <div className="extension-secondary-panel">
            <Row className="align-items-center">
              <Col lg={4}>
                <div className="section-title extension-section-intro mb-4 mb-lg-0">
                  <Badge pill className="extension-badge mb-3">
                    More reasons to keep it installed
                  </Badge>
                  <h3 className="title extension-section-heading mb-4">
                    More than a word saver
                  </h3>
                  <p className="sec-title extension-section-lead">
                    HighVocab keeps learning practical with lightweight tools
                    that support reading, recall, and clearer writing.
                  </p>
                </div>
              </Col>
              <Col lg={8}>
                <Row className="g-4">
                  {extraBenefits.map((benefit) => (
                    <Col md={4} key={benefit.title}>
                      <div className="extension-card extension-compact-card h-100">
                        <img src={benefit.image} alt={benefit.title} />
                        <span className="extension-icon-wrap mt-4 mb-3">
                          <FeatherIcon icon={benefit.icon} size={18} />
                        </span>
                        <h4>{benefit.title}</h4>
                        <p>{benefit.description}</p>
                      </div>
                    </Col>
                  ))}
                </Row>
              </Col>
            </Row>
          </div>
        </Container>

        {!FREE_VERSION && <Pricing />}

        <section className="section pt-0" id="highvocab-faq">
          <Container className="mt-100 mt-60 md-padding-20">
            <Row className="justify-content-center">
              <Col lg={8} className="text-center">
                <div className="section-title extension-section-intro">
                  <Badge pill className="extension-badge mb-3">
                    FAQ
                  </Badge>
                  <h3 className="title extension-section-heading mb-4">
                    Questions before you install?
                  </h3>
                </div>
              </Col>
            </Row>

            <Row className="justify-content-center">
              <Col lg={9} className="mt-4 pt-2">
                <div className="faq-content">
                  <div className="accordion" id="accordionExample">
                    {faqs.map((faq) => {
                      const isOpen = openFaq === faq.id
                      return (
                        <Card className="border-0 rounded mb-3" key={faq.id}>
                          <Link
                            to="#"
                            onClick={() =>
                              setOpenFaq(isOpen ? null : faq.id)
                            }
                            className={
                              isOpen
                                ? 'faq position-relative text-primary'
                                : 'faq position-relative text-dark'
                            }
                          >
                            <CardHeader className="border-0 shadow bg-light p-3">
                              <h6 className="title mb-0">
                                {faq.question}
                                <i
                                  className={
                                    isOpen
                                      ? 'mdi mdi-chevron-up float-end'
                                      : 'mdi mdi-chevron-down float-end'
                                  }
                                ></i>
                              </h6>
                            </CardHeader>
                          </Link>
                          <Collapse isOpen={isOpen}>
                            <CardBody>
                              <p className="text-muted mb-0 faq-ans">
                                {faq.answer}
                              </p>
                            </CardBody>
                          </Collapse>
                        </Card>
                      )
                    })}
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        <Testi />
      </div>

      <div className="position-relative">
        <div className="shape overflow-hidden text-footer">
          <svg
            viewBox="0 0 2880 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0 48H1437.5H2880V0H2160C1442.5 52 720 0 720 0H0V48Z"
              fill="currentColor"
            ></path>
          </svg>
        </div>
      </div>

      <BackToTop />
    </React.Fragment>
  )
}

export default Index
